// Cars — the whole-car business.
//
// Not to be confused with stockApi: that one deals in DONOR cars, bought to be
// cut up and sold part by part. These are cars sold as cars, either CAP's own
// or someone else's on commission.

import axiosInstance from './axiosInstance';
import carsAxiosInstance from './carsAxiosInstance';
import { API_ENDPOINTS } from '../config/api.config';
import StorageUtil from '../utils/storage';

const { CARS, CARS_TEAM } = API_ENDPOINTS;

export const carsApi = {
  /** GET /admin/cars/?status=&ownership=&q=&date_from=&date_to= → { cars, summary } */
  getList: (params = {}) => axiosInstance.get(CARS.LIST, { params }),
  /** GET /admin/cars/{id}/ → car + sale + expenses + pnl */
  getDetail: (id) => axiosInstance.get(CARS.DETAIL(id)),
  /** POST /admin/cars/ */
  create: (data) => axiosInstance.post(CARS.LIST, data),
  /** PATCH /admin/cars/{id}/ */
  update: (id, data) => axiosInstance.patch(CARS.DETAIL(id), data),
  /** DELETE /admin/cars/{id}/ — password required; refused once the car is sold */
  remove: (id, data) => axiosInstance.delete(CARS.DETAIL(id), { data }),

  /** POST /admin/cars/{id}/sell/ — the commission is frozen onto the sale here */
  sell: (id, data) => axiosInstance.post(CARS.SELL(id), data),
  /** PATCH /admin/cars/sales/{saleId}/ — correct it, collect the balance, pay the owner */
  updateSale: (saleId, data) => axiosInstance.patch(CARS.SALE(saleId), data),
  /** DELETE /admin/cars/sales/{saleId}/ — undo; the car goes back on the lot */
  undoSale: (saleId, data) => axiosInstance.delete(CARS.SALE(saleId), { data }),

  /** GET/POST /admin/cars/{id}/expenses/ — work done on one car (Cars' money) */
  getExpenses: (id) => axiosInstance.get(CARS.EXPENSES(id)),
  addExpense: (id, data) => axiosInstance.post(CARS.EXPENSES(id), data),
  removeExpense: (id, expenseId) => axiosInstance.delete(CARS.EXPENSE(id, expenseId)),

  /** GET /admin/cars/summary/?date_from=&date_to= */
  getSummary: (params = {}) => axiosInstance.get(CARS.SUMMARY, { params }),

  /** GET /sales/cars/ → { brands, all_parts } — the SAME catalog the
      salesperson inquiry form picks from, so a Swift is the same Swift here. */
  getCatalog: () => axiosInstance.get(CARS.CATALOG),
};

// CAP ↔ Cars float. Giving money writes a real expense that day; getting it
// back is added straight to the balance. `outstanding` is what the car
// business is holding of CAP's money right now.
export const carsFundApi = {
  /** GET /admin/cars/fund-transfers/ → { transfers, given, returned, outstanding } */
  getList: (params = {}) => axiosInstance.get(CARS.FUND_TRANSFERS, { params }),
  /** POST /admin/cars/fund-transfers/ { direction: 'to_cars'|'to_cap', amount, transferred_on?, note?, car? } */
  create: (data) => axiosInstance.post(CARS.FUND_TRANSFERS, data),
  /** DELETE /admin/cars/fund-transfers/{id}/ — takes its expense row with it */
  remove: (id, data = {}) => axiosInstance.delete(CARS.FUND_TRANSFER(id), { data }),
};

// ── The cars team's own workspace ────────────────────────────────────────────
// Their login, their lot. Everything here goes through carsAxiosInstance, so
// it carries the CARS token and reaches only the endpoints their token allows.
export const carsTeamAuth = {
  /** POST /cars/login/ { login, password } — `login` is a username, a phone
      or an email; the backend matches whichever it turns out to be. */
  login: async ({ login, phone_number, email, password }) => {
    const { data } = await carsAxiosInstance.post(CARS_TEAM.LOGIN, {
      ...(login ? { login } : {}),
      ...(phone_number ? { phone_number } : {}),
      ...(email ? { email } : {}),
      password,
    });
    StorageUtil.setCarsAccessToken(data.access_token);
    if (data.refresh_token) StorageUtil.setCarsRefreshToken(data.refresh_token);
    StorageUtil.setCarsStaff(data.staff);
    return data;
  },
  /** POST /cars/logout/ — clears locally even if the call fails, so a network
      blip can never leave someone signed in on a shared machine. */
  logout: async () => {
    try {
      await carsAxiosInstance.post(CARS_TEAM.LOGOUT);
    } catch (_) {
      /* ignore — the local session goes either way */
    } finally {
      StorageUtil.clearCarsAuth();
    }
  },
};

export const carsTeamApi = {
  /** GET /cars/inventory/?status=&ownership=&q= → { cars, stock } */
  getInventory: (params = {}) => carsAxiosInstance.get(CARS_TEAM.INVENTORY, { params }),
  /** POST /cars/inventory/ — add a car to the lot */
  addCar: (data) => carsAxiosInstance.post(CARS_TEAM.INVENTORY, data),
  /** GET /cars/inventory/{id}/ */
  getCar: (id) => carsAxiosInstance.get(CARS_TEAM.CAR(id)),
  /** PATCH /cars/inventory/{id}/ */
  updateCar: (id, data) => carsAxiosInstance.patch(CARS_TEAM.CAR(id), data),
  /** DELETE /cars/inventory/{id}/ — { reason }. Refused while the car holds a
      sale; snapshotted to Deleted Records under the staff member's name. */
  removeCar: (id, reason = '') =>
    carsAxiosInstance.delete(CARS_TEAM.CAR(id), { data: { reason } }),
  /** POST /cars/inventory/{id}/sell/ */
  sell: (id, data) => carsAxiosInstance.post(CARS_TEAM.SELL(id), data),
  /** PATCH the sale a car has — price, buyer, terms, or {balance_paid: true} */
  updateSale: (id, data) => carsAxiosInstance.patch(CARS_TEAM.SALE(id), data),
  /** DELETE the sale — the car goes back on the lot. { reason } */
  undoSale: (id, reason = '') =>
    carsAxiosInstance.delete(CARS_TEAM.SALE(id), { data: { reason } }),

  /** POST one receipt of the buyer's money — { amount, mode, bank_name?, paid_on?, note? }.
      The car flips to SOLD by itself the moment nothing is owed. */
  addPayment: (id, data) => carsAxiosInstance.post(CARS_TEAM.PAYMENTS(id), data),
  /** DELETE a receipt entered wrong — { reason }. The car goes back to BOOKED
      if the removal leaves money owing. */
  removePayment: (id, paymentId, reason = '') =>
    carsAxiosInstance.delete(CARS_TEAM.PAYMENT(id, paymentId), { data: { reason } }),

  /** POST the handover — { aadhaar? }. Only once nothing is owed; generates
      the delivery-note PDF and hosts it for WhatsApp. */
  deliver: (id, data = {}) => carsAxiosInstance.post(CARS_TEAM.DELIVER(id), data),
  /** DELETE — delivered by mistake; the handover is un-marked. */
  undoDeliver: (id) => carsAxiosInstance.delete(CARS_TEAM.DELIVER(id)),
  /** GET the delivery note itself, as PDF bytes. */
  downloadDeliveryNote: (id) =>
    carsAxiosInstance.get(CARS_TEAM.DELIVERY_NOTE(id), { responseType: 'blob' }),

  /** GET the booking confirmation PDF — fresh, as the sale stands now. */
  downloadBookingNote: (id) =>
    carsAxiosInstance.get(CARS_TEAM.BOOKING_NOTE(id), { responseType: 'blob' }),
  /** POST → { url } — hosts the current booking note for the WhatsApp share. */
  shareBookingNote: (id) => carsAxiosInstance.post(CARS_TEAM.BOOKING_NOTE(id)),

  /** GET /cars/inquiries/?status=&waiting=&car=&q= → { inquiries, ready_to_call } */
  getInquiries: (params = {}) => carsAxiosInstance.get(CARS_TEAM.INQUIRIES, { params }),
  /** POST — a customer rang: what they want, or which car they asked about */
  addInquiry: (data) => carsAxiosInstance.post(CARS_TEAM.INQUIRIES, data),
  /** PATCH — move it along: contacted, closed, re-opened, or edit the note */
  updateInquiry: (id, data) => carsAxiosInstance.patch(CARS_TEAM.INQUIRY(id), data),
  /** DELETE — noted by mistake */
  removeInquiry: (id) => carsAxiosInstance.delete(CARS_TEAM.INQUIRY(id)),
  /** GET /cars/summary/ — what is on the lot and what went today */
  getSummary: () => carsAxiosInstance.get(CARS_TEAM.SUMMARY),
  /** The catalog is public, so the team gets the same picker the sales app has */
  getCatalog: () => carsAxiosInstance.get(CARS.CATALOG),
};

// The cars team's logins, managed BY THE SUPERADMIN — same shape as the
// walk-in entry team's.
export const carsStaffApi = {
  getList: (params = {}) => axiosInstance.get(CARS.STAFF, { params }),
  create: (data) => axiosInstance.post(CARS.STAFF, data),
  update: (id, data) => axiosInstance.patch(CARS.STAFF_DETAIL(id), data),
  /** DELETE — a SOFT delete, so their cars keep saying who added them */
  remove: (id) => axiosInstance.delete(CARS.STAFF_DETAIL(id)),
  restore: (id) => axiosInstance.post(CARS.STAFF_RESTORE(id)),
  setActive: (id, isActive) => axiosInstance.patch(CARS.STAFF_ACTIVE(id), { is_active: isActive }),
};
